import React, { Component } from "react";
import { Input } from "reactstrap";
import { getLoyaltyConfig, updateLoyaltyConfig } from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Table, Chip, Btn, Field, Toggle,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Loyalty configuration — the rules screen, restyled onto the console shell.
 *
 * The shape of the form is unchanged: a master switch and global rates, a row
 * of per-order-type overrides, and expiry. What changed is that the rules now
 * read as rows with a switch on the right, matching the rest of the console's
 * settings screens, and the save action lives in the topbar where it is always
 * reachable without scrolling four panels.
 */

const ORDER_TYPES = [
  { key: "food", label: "Food orders", icon: "restaurant" },
  { key: "table", label: "Table booking", icon: "table_restaurant" },
  { key: "room", label: "Room booking", icon: "hotel" },
  { key: "transport", label: "Transport", icon: "local_taxi" },
];

// null (inherit global) is shown as an empty input; "" maps back to null on save.
const numOrNull = (v) => (v === null || v === undefined ? "" : v);
const toNumOrNull = (v) => (v === "" || v === null ? null : Number(v));

/** Console-surface form fields, plus the narrow inline inputs in the rules table. */
const CSS = `
.loy-set .form-control{height:46px;padding:0 15px;background:#fff;border:1px solid #e6d8c4;
  border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.loy-set .form-control:focus{border-color:${C.sand};box-shadow:none}
.loy-set .form-control::placeholder{color:${C.taupeLight}}
.loy-set .form-control:disabled{background:#f7f1e7;color:${C.taupeLight}}
.loy-set .loy-narrow .form-control{height:38px;font-size:14px;border-radius:10px;max-width:130px}
`;

class LoyaltySettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      isSaving: false,
      hasChanges: false,
      form: {
        enabled: true,
        earnRatePerCurrency: 1,
        redeemValuePerPoint: 0.01,
        minRedeemPoints: 100,
        maxRedeemPercent: 100,
        expiryEnabled: false,
        expiryDays: 365,
        orderTypes: {
          food: { enabled: true, earnRatePerCurrency: "", redeemable: true, maxRedeemPercent: "" },
          table: { enabled: true, earnRatePerCurrency: "", redeemable: true, maxRedeemPercent: "" },
          room: { enabled: true, earnRatePerCurrency: "", redeemable: true, maxRedeemPercent: "" },
          transport: { enabled: true, earnRatePerCurrency: "", redeemable: true, maxRedeemPercent: "" },
        },
      },
    };
  }

  componentDidMount() {
    this.loadConfig();
  }

  loadConfig = () => {
    getLoyaltyConfig()
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const c = result.data;
          const ot = c.orderTypes || {};
          const orderTypes = {};
          ORDER_TYPES.forEach(({ key }) => {
            const t = ot[key] || {};
            orderTypes[key] = {
              enabled: t.enabled !== false,
              earnRatePerCurrency: numOrNull(t.earnRatePerCurrency),
              redeemable: t.redeemable !== false,
              maxRedeemPercent: numOrNull(t.maxRedeemPercent),
            };
          });
          this.setState({
            isLoaded: true,
            hasChanges: false,
            form: {
              enabled: c.enabled !== false,
              earnRatePerCurrency: c.earnRatePerCurrency ?? 1,
              redeemValuePerPoint: c.redeemValuePerPoint ?? 0.01,
              minRedeemPoints: c.minRedeemPoints ?? 100,
              maxRedeemPercent: c.maxRedeemPercent ?? 100,
              expiryEnabled: !!c.expiryEnabled,
              expiryDays: c.expiryDays ?? 365,
              orderTypes,
            },
          });
        } else {
          this.setState({ isLoaded: true });
          toast.info("No loyalty config found. Default values loaded.");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading loyalty config");
      });
  };

  setGlobal = (field, value) => {
    this.setState((prev) => ({
      form: { ...prev.form, [field]: value },
      hasChanges: true,
    }));
  };

  setType = (type, field, value) => {
    this.setState((prev) => ({
      form: {
        ...prev.form,
        orderTypes: {
          ...prev.form.orderTypes,
          [type]: { ...prev.form.orderTypes[type], [field]: value },
        },
      },
      hasChanges: true,
    }));
  };

  handleSave = () => {
    const { form } = this.state;

    if (Number(form.earnRatePerCurrency) < 0) {
      toast.error("Global earn rate must be 0 or more");
      return;
    }
    if (Number(form.redeemValuePerPoint) <= 0) {
      toast.error("Redeem value per point must be greater than 0");
      return;
    }
    if (Number(form.minRedeemPoints) < 0) {
      toast.error("Minimum redeem points must be 0 or more");
      return;
    }
    if (Number(form.maxRedeemPercent) < 0 || Number(form.maxRedeemPercent) > 100) {
      toast.error("Max redeem percent must be between 0 and 100");
      return;
    }
    if (form.expiryEnabled && (!Number.isInteger(Number(form.expiryDays)) || Number(form.expiryDays) < 1)) {
      toast.error("Expiry days must be a positive whole number");
      return;
    }

    const orderTypes = {};
    ORDER_TYPES.forEach(({ key }) => {
      const t = form.orderTypes[key];
      orderTypes[key] = {
        enabled: !!t.enabled,
        earnRatePerCurrency: toNumOrNull(t.earnRatePerCurrency),
        redeemable: !!t.redeemable,
        maxRedeemPercent: toNumOrNull(t.maxRedeemPercent),
      };
    });

    const payload = {
      enabled: !!form.enabled,
      earnRatePerCurrency: Number(form.earnRatePerCurrency),
      redeemValuePerPoint: Number(form.redeemValuePerPoint),
      minRedeemPoints: Number(form.minRedeemPoints),
      maxRedeemPercent: Number(form.maxRedeemPercent),
      expiryEnabled: !!form.expiryEnabled,
      expiryDays: Number(form.expiryDays),
      orderTypes,
    };

    this.setState({ isSaving: true });
    updateLoyaltyConfig(payload)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Loyalty settings saved");
          this.setState({ hasChanges: false });
        } else {
          toast.error(response?.message || "Failed to save settings");
        }
        this.setState({ isSaving: false });
      })
      .catch(() => {
        toast.error("Error saving settings");
        this.setState({ isSaving: false });
      });
  };

  /** A rule row: a caption on the left, the control on the right. */
  ruleRow = (label, hint, control, last) => (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 16, padding: "14px 0",
        borderBottom: last ? "none" : `1px solid ${C.panel}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: C.ink }}>{label}</div>
        {hint && <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>{hint}</div>}
      </div>
      <div className="loy-narrow" style={{ flex: "0 0 auto" }}>{control}</div>
    </div>
  );

  numberInput = (field, props = {}) => (
    <Input
      type="number"
      {...props}
      value={this.state.form[field]}
      disabled={!canWrite() || !!props.disabled}
      onChange={(e) => this.setGlobal(field, e.target.value)}
      style={{ width: 130 }}
    />
  );

  typeColumns() {
    const write = canWrite();
    return [
      {
        key: "label",
        label: "Order type",
        render: (t) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              className="ms"
              style={{ fontSize: 19, color: C.brown, background: C.iconTint, padding: 7, borderRadius: 10 }}
            >
              {t.icon}
            </span>
            <span style={{ fontSize: 15, color: C.ink }}>{t.label}</span>
          </div>
        ),
      },
      {
        key: "enabled",
        label: "Earns points",
        width: 120,
        render: (t) => (
          <Toggle
            on={this.state.form.orderTypes[t.key].enabled}
            disabled={!write}
            title={`${t.label} earns points`}
            onClick={() => this.setType(t.key, "enabled", !this.state.form.orderTypes[t.key].enabled)}
          />
        ),
      },
      {
        key: "earnRate",
        label: "Earn rate",
        width: 160,
        render: (t) => (
          <div className="loy-narrow">
            <Input
              type="number"
              step="0.1"
              min="0"
              placeholder="global"
              disabled={!write}
              value={this.state.form.orderTypes[t.key].earnRatePerCurrency}
              onChange={(e) => this.setType(t.key, "earnRatePerCurrency", e.target.value)}
            />
          </div>
        ),
      },
      {
        key: "redeemable",
        label: "Redeemable",
        width: 120,
        render: (t) => (
          <Toggle
            on={this.state.form.orderTypes[t.key].redeemable}
            disabled={!write}
            title={`Points can be spent on ${t.label}`}
            onClick={() => this.setType(t.key, "redeemable", !this.state.form.orderTypes[t.key].redeemable)}
          />
        ),
      },
      {
        key: "maxRedeem",
        label: "Max redeem %",
        width: 160,
        render: (t) => (
          <div className="loy-narrow">
            <Input
              type="number"
              min="0"
              max="100"
              placeholder="global"
              disabled={!write}
              value={this.state.form.orderTypes[t.key].maxRedeemPercent}
              onChange={(e) => this.setType(t.key, "maxRedeemPercent", e.target.value)}
            />
          </div>
        ),
      },
    ];
  }

  render() {
    const { isLoaded, isSaving, hasChanges, form } = this.state;
    const write = canWrite();

    return (
      <ConsolePage
        title="Loyalty settings"
        subtitle="Points are shared across every branch of the hotel"
        actions={
          <React.Fragment>
            {hasChanges && (
              <Chip tone="warn">
                <span className="ms" style={{ fontSize: 16 }}>edit_note</span>
                Unsaved changes
              </Chip>
            )}
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/loyalty/accounts")}>
              Members
            </Btn>
            {write && (
              <Btn icon="save" onClick={this.handleSave} disabled={isSaving || !hasChanges}>
                {isSaving ? "Saving…" : "Save settings"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        {!isLoaded ? (
          <Panel>
            <div style={{ padding: "40px 0", textAlign: "center", color: C.taupeLight }}>
              Loading settings…
            </div>
          </Panel>
        ) : (
          <div className="loy-set" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, alignItems: "start" }}>
              <Panel title="Programme & earning" subtitle="What a guest gets back for spending">
                {this.ruleRow(
                  "Loyalty programme",
                  form.enabled
                    ? "On — points are earned and can be spent"
                    : "Off — nothing is earned or redeemed anywhere",
                  <Toggle
                    on={form.enabled}
                    disabled={!write}
                    title="Loyalty programme master switch"
                    onClick={() => this.setGlobal("enabled", !form.enabled)}
                  />
                )}
                {this.ruleRow(
                  "Global earn rate",
                  "Points per 1 currency unit. Used by any order type without its own rate.",
                  this.numberInput("earnRatePerCurrency", { step: "0.1", min: "0" }),
                  true
                )}
              </Panel>

              <Panel title="Redemption" subtitle="What a point is worth when it is spent">
                {this.ruleRow(
                  "Redeem value per point",
                  "0.01 means 100 points = 1 currency unit",
                  this.numberInput("redeemValuePerPoint", { step: "0.01", min: "0" })
                )}
                {this.ruleRow(
                  "Minimum points to redeem",
                  "A guest below this cannot spend anything",
                  this.numberInput("minRedeemPoints", { min: "0" })
                )}
                {this.ruleRow(
                  "Global max redeem",
                  "The most of an order's value points may cover, as a percentage",
                  this.numberInput("maxRedeemPercent", { min: "0", max: "100" }),
                  true
                )}
              </Panel>
            </div>

            <Panel style={{ padding: 0 }}>
              <div style={{ padding: "22px 22px 6px" }}>
                <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Per order type</div>
                <div style={{ marginTop: 4, fontSize: 13, color: C.taupeLight }}>
                  Override earning and redemption per service. Leave a rate or cap blank to
                  inherit the global value.
                </div>
              </div>
              <div style={{ padding: "0 12px 10px" }}>
                <Table
                  columns={this.typeColumns()}
                  rows={ORDER_TYPES}
                  rowKey="key"
                  empty="No order types configured"
                />
              </div>
            </Panel>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, alignItems: "start" }}>
              <Panel title="Points expiry" subtitle="Off by default — points never expire until enabled">
                {this.ruleRow(
                  "Expire points after a period",
                  form.expiryEnabled
                    ? "Oldest points are expired first, once a day"
                    : "Points stay on the account indefinitely",
                  <Toggle
                    on={form.expiryEnabled}
                    disabled={!write}
                    title="Expire points"
                    onClick={() => this.setGlobal("expiryEnabled", !form.expiryEnabled)}
                  />
                )}
                {this.ruleRow(
                  "Expire after",
                  "Days a point may sit unused before it is written off",
                  this.numberInput("expiryDays", { min: "1", disabled: !form.expiryEnabled }),
                  true
                )}
              </Panel>

              <Panel>
                <Field label="Scope">
                  <div style={{ fontSize: 14, color: C.slate, lineHeight: 1.6 }}>
                    Every rule on this page applies to the whole hotel. Points a guest earns at
                    one branch can be spent at any other.
                  </div>
                </Field>
                <div style={{ marginTop: 18 }}>
                  {write ? (
                    <Btn
                      icon="save"
                      onClick={this.handleSave}
                      disabled={isSaving || !hasChanges}
                      style={{ width: "100%" }}
                    >
                      {isSaving ? "Saving…" : hasChanges ? "Save settings" : "Nothing to save"}
                    </Btn>
                  ) : (
                    <div style={{ fontSize: 13, color: C.taupeLight }}>
                      Your account can read these rules but not change them.
                    </div>
                  )}
                </div>
              </Panel>
            </div>
          </div>
        )}
      </ConsolePage>
    );
  }
}

export default LoyaltySettings;
